(() => {
  'use strict';

  const theme = window.MarlowTheme || {};
  const liveRegion = document.getElementById('MarlowLiveRegion');

  let lockedScrollY = 0;
  let lockCount = 0;
  const lockScroll = () => {
    if (lockCount === 0) {
      lockedScrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${lockedScrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.classList.add('is-locked');
    }
    lockCount += 1;
  };
  const unlockScroll = () => {
    lockCount = Math.max(0, lockCount - 1);
    if (lockCount === 0) {
      document.body.classList.remove('is-locked');
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      window.scrollTo({ top: lockedScrollY, behavior: 'instant' });
    }
  };

  class CollectionFilter extends HTMLElement {
    connectedCallback() {
      this.tabs = [...this.querySelectorAll('[data-filter-type]')];
      this.cards = [...this.querySelectorAll('[data-product-type]')];
      this.querySelector('.collection-tabs')?.addEventListener('click', (event) => {
        const button = event.target.closest('[data-filter-type]');
        if (!button) return;
        this.setFilter(button.dataset.filterType, button);
      });
    }

    setFilter(type, trigger) {
      this.tabs.forEach((tab) => tab.classList.toggle('is-active', tab === trigger));
      this.cards.forEach((card) => {
        const match = !type || card.dataset.productType === type;
        card.classList.toggle('hidden', !match);
      });
    }
  }

  class CornerNav extends HTMLElement {
    connectedCallback() {
      this.toggles = [...document.querySelectorAll('[data-corner-nav-toggle]')];
      this.toggles.forEach((toggle) => toggle.addEventListener('click', () => this.set(!this.classList.contains('is-open'))));
      this.querySelectorAll('[data-corner-nav-close]').forEach((el) => el.addEventListener('click', () => this.set(false)));
      this.list = this.querySelector('#CornerNavList');
      this.list?.addEventListener('click', (event) => {
        if (event.target.closest('a, span')) { this.set(false); return; }
        if (event.target === this.list) this.set(false);
      });
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.set(false);
      });
    }

    set(open) {
      this.classList.toggle('is-open', open);
      this.toggles.forEach((toggle) => {
        toggle.classList.toggle('is-open', open);
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
      if (open) lockScroll(); else unlockScroll();
    }
  }

  class ProductDetail extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('[data-product-json]');
      try { this.variants = JSON.parse(json?.textContent || '[]'); } catch (_) { this.variants = []; }
      this.form = this.querySelector('.product-form');
      this.optionGroups = [...this.querySelectorAll('[data-option-index]')];
      this.optionGroups.forEach((group) => group.addEventListener('click', (event) => {
        const button = event.target.closest('[data-option-value]');
        if (!button) return;
        group.querySelectorAll('[data-option-value]').forEach((node) => {
          node.classList.toggle('is-selected', node === button);
          node.setAttribute('aria-pressed', node === button ? 'true' : 'false');
        });
        this.updateVariant();
      }));
      this.querySelectorAll('[data-media-target]').forEach((button) => button.addEventListener('click', () => this.showMedia(button.dataset.mediaTarget)));
      this.querySelectorAll('[data-thumb-scroll]').forEach((button) => button.addEventListener('click', () => {
        const rail = this.querySelector('.product-gallery__thumbs');
        rail?.scrollBy({ top: Number(button.dataset.thumbScroll) * 110, left: 0, behavior: 'smooth' });
      }));
      this.querySelectorAll('[data-dialog-open]').forEach((button) => button.addEventListener('click', () => this.openDrawer(document.getElementById(button.dataset.dialogOpen))));
      this.querySelectorAll('[data-dialog-close]').forEach((button) => button.addEventListener('click', () => this.closeDrawer(button.closest('.size-drawer'))));
      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        this.querySelectorAll('.size-drawer.is-open').forEach((drawer) => this.closeDrawer(drawer));
      });
      this.initCarousel();
    }

    initCarousel() {
      const gallery = this.querySelector('[data-gallery]');
      const slides = gallery ? [...gallery.children] : [];
      this.gallery = gallery;
      this.slides = slides;
      if (!gallery || slides.length < 2) return;
      const dots = [...this.querySelectorAll('[data-carousel-dot]')];
      const goTo = (index) => {
        const target = slides[Math.max(0, Math.min(slides.length - 1, index))];
        if (!target) return;
        const axis = this.scrollAxis(gallery);
        if (axis === 'x') gallery.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
        else if (axis === 'y') gallery.scrollTo({ top: target.offsetTop, behavior: 'smooth' });
        else target.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
      };
      this.goTo = goTo;
      this.querySelector('[data-carousel-prev]')?.addEventListener('click', () => goTo(slides.indexOf(this.activeSlide(gallery, slides)) - 1));
      this.querySelector('[data-carousel-next]')?.addEventListener('click', () => goTo(slides.indexOf(this.activeSlide(gallery, slides)) + 1));
      dots.forEach((dot, index) => dot.addEventListener('click', () => goTo(index)));
      slides.forEach((slide) => slide.addEventListener('click', (event) => {
        if (event.target.closest('video, model-viewer, a, button, iframe')) return;
        const current = slides.indexOf(this.activeSlide(gallery, slides));
        goTo((current + 1) % slides.length);
      }));
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const index = slides.indexOf(entry.target);
            dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
          });
        }, { root: gallery, threshold: .6 });
        slides.forEach((slide) => observer.observe(slide));
      }
    }

    scrollAxis(gallery) {
      const cs = getComputedStyle(gallery);
      if (cs.overflowX === 'auto' || cs.overflowX === 'scroll') return 'x';
      if (cs.overflowY === 'auto' || cs.overflowY === 'scroll') return 'y';
      return 'page';
    }

    activeSlide(gallery, slides) {
      const axis = this.scrollAxis(gallery);
      if (axis === 'page') {
        const center = window.innerHeight / 2;
        return slides.reduce((closest, slide) => {
          const rect = slide.getBoundingClientRect();
          const dist = Math.abs((rect.top + rect.bottom) / 2 - center);
          const closestDist = Math.abs((closest.getBoundingClientRect().top + closest.getBoundingClientRect().bottom) / 2 - center);
          return dist < closestDist ? slide : closest;
        }, slides[0]);
      }
      const galRect = gallery.getBoundingClientRect();
      return slides.find((slide) => {
        const rect = slide.getBoundingClientRect();
        return axis === 'y' ? Math.abs(rect.top - galRect.top) < 20 : Math.abs(rect.left - galRect.left) < 20;
      }) || slides[0];
    }

    openDrawer(drawer) {
      if (!drawer) return;
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      lockScroll();
    }

    closeDrawer(drawer) {
      if (!drawer) return;
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      unlockScroll();
    }

    updateVariant() {
      if (!this.optionGroups.length) return;
      const selected = this.optionGroups.map((group) => group.querySelector('.is-selected')?.dataset.optionValue);
      const idInput = this.querySelector('[data-variant-id]');
      const submit = this.querySelector('[data-add-to-cart]');
      const label = this.querySelector('[data-add-to-cart-text]');

      if (selected.some((value) => !value)) {
        if (submit) submit.disabled = false;
        if (label) label.textContent = theme.strings.selectSize;
        return;
      }

      const variant = this.variants.find((entry) => entry.options.every((value, index) => value === selected[index]));
      if (!variant) {
        if (submit) submit.disabled = true;
        if (label) label.textContent = theme.strings.unavailable;
        return;
      }
      if (idInput) idInput.value = variant.id;
      if (submit) submit.disabled = !variant.available;
      if (label) label.textContent = variant.available ? theme.strings.addToCart : theme.strings.soldOut;
      if (variant.featured_media?.id) this.showMedia(variant.featured_media.id);
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }

    showMedia(id) {
      this.querySelectorAll('[data-media-target]').forEach((thumb) => thumb.classList.toggle('is-active', String(thumb.dataset.mediaTarget) === String(id)));
      if (this.slides && this.goTo) {
        const index = this.slides.findIndex((slide) => String(slide.dataset.mediaId) === String(id));
        if (index > -1) this.goTo(index);
      }
    }
  }

  class CartPageEl extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (event) => this.onAction(event));
      const checkbox = this.querySelector('[data-agree-checkbox]');
      const checkoutButton = this.querySelector('[data-checkout-button]');
      const agreeLabel = this.querySelector('[data-cart-agree]');
      checkbox?.addEventListener('change', () => {
        if (checkoutButton) checkoutButton.setAttribute('aria-disabled', checkbox.checked ? 'false' : 'true');
      });

      if (checkoutButton) {
        checkoutButton.addEventListener('click', (event) => {
          if (checkoutButton.getAttribute('aria-disabled') !== 'true') return;
          event.preventDefault();
          agreeLabel?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          agreeLabel?.classList.remove('is-attention');
          void agreeLabel?.offsetWidth;
          agreeLabel?.classList.add('is-attention');
          if (liveRegion) liveRegion.textContent = 'Ödemeye geçmeden önce sözleşme ve politikaları kabul etmeniz gerekiyor.';
        });
      }

      this.campaignModal = this.querySelector('[data-cart-campaign-modal]');
      this.campaignConfirmed = false;
      if (checkoutButton && this.campaignModal) {
        checkoutButton.addEventListener('click', (event) => {
          if (checkoutButton.getAttribute('aria-disabled') === 'true') return;
          if (!this.campaignConfirmed) {
            event.preventDefault();
            this.openCampaignModal();
          }
        });
        this.querySelectorAll('[data-cart-campaign-close]').forEach((el) => el.addEventListener('click', () => this.closeCampaignModal()));
        this.querySelector('[data-cart-campaign-continue]')?.addEventListener('click', () => {
          this.campaignConfirmed = true;
          this.closeCampaignModal();
          const form = this.querySelector('form');
          if (form) form.requestSubmit(checkoutButton);
        });
      }
      this.querySelectorAll('[data-quick-add]').forEach((button) => button.addEventListener('click', () => this.quickAdd(button)));
    }

    openCampaignModal() {
      this.campaignModal.classList.add('is-open');
      this.campaignModal.setAttribute('aria-hidden', 'false');
      lockScroll();
    }

    closeCampaignModal() {
      this.campaignModal.classList.remove('is-open');
      this.campaignModal.setAttribute('aria-hidden', 'true');
      unlockScroll();
    }

    async quickAdd(button) {
      const variantId = button.dataset.quickAdd;
      if (!variantId) return;
      button.disabled = true;
      const originalText = button.textContent;
      try {
        const response = await fetch(`${theme.routes.cartAdd}.js`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1 })
        });
        if (!response.ok) throw new Error('Add failed');
        button.textContent = 'EKLENDİ';
      } catch (_) {
        button.disabled = false;
        button.textContent = originalText;
        if (liveRegion) liveRegion.textContent = 'Ürün eklenemedi.';
      }
    }

    async onAction(event) {
      const quantityButton = event.target.closest('[data-quantity-change]');
      const removeButton = event.target.closest('[data-remove-item]');
      if (!quantityButton && !removeButton) return;
      const row = event.target.closest('[data-line]');
      if (!row) return;
      const current = Number(row.querySelector('[data-line-qty]')?.textContent || 0);
      const quantity = removeButton ? 0 : Math.max(0, current + Number(quantityButton.dataset.quantityChange));
      row.style.opacity = '.45';
      try {
        const response = await fetch(`${theme.routes.cartChange}.js`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line: Number(row.dataset.line), quantity })
        });
        if (!response.ok) throw new Error('Cart unavailable');
        // Reload rather than patch the DOM in place: Shopify can split or merge
        // cart lines (discount tiers, quantity breaks), so line count/order can
        // change in ways a partial DOM patch would render incorrectly.
        window.location.reload();
      } catch (_) {
        row.style.opacity = '';
        if (liveRegion) liveRegion.textContent = 'Sepet güncellenemedi.';
      }
    }
  }

  class SizeChartEl extends HTMLElement {
    connectedCallback() {
      this.querySelectorAll('[data-category-select]').forEach((button) => button.addEventListener('click', () => this.setCategory(button.dataset.categorySelect, button)));
    }

    setCategory(index, trigger) {
      this.querySelectorAll('[data-category-select]').forEach((button) => button.classList.toggle('is-active', button === trigger));
      this.querySelectorAll('[data-category-panel]').forEach((panel) => panel.classList.toggle('is-active', panel.dataset.categoryPanel === index));
      const row = this.querySelector('.size-chart__categories');
      if (row && trigger) {
        const target = trigger.offsetLeft - (row.clientWidth - trigger.offsetWidth) / 2;
        row.scrollTo({ left: target, behavior: 'smooth' });
      }
    }
  }

  class CampaignPopup extends HTMLElement {
    connectedCallback() {
      this.querySelectorAll('[data-campaign-close]').forEach((el) => el.addEventListener('click', () => this.close()));
      document.addEventListener('keydown', (event) => { if (event.key === 'Escape') this.close(); });

      const isCollectionPage = document.body.classList.contains('template-collection') || document.body.classList.contains('template-list-collections');
      const intervalHours = Number(this.dataset.intervalHours) || 24;
      const delaySeconds = Number(this.dataset.delaySeconds) || 7;
      let dueForShow = true;
      try {
        const lastShown = Number(localStorage.getItem('marlowCampaignPopupLastShown') || 0);
        dueForShow = Date.now() - lastShown > intervalHours * 60 * 60 * 1000;
      } catch (_) {}
      if (isCollectionPage && dueForShow) {
        this.openTimer = window.setTimeout(() => this.open(), delaySeconds * 1000);
      }
    }

    disconnectedCallback() {
      window.clearTimeout(this.openTimer);
    }

    open() {
      this.classList.add('is-open');
      lockScroll();
      try { localStorage.setItem('marlowCampaignPopupLastShown', String(Date.now())); } catch (_) {}
    }

    close() {
      this.classList.remove('is-open');
      unlockScroll();
    }
  }

  if (!customElements.get('collection-filter')) customElements.define('collection-filter', CollectionFilter);
  if (!customElements.get('corner-nav')) customElements.define('corner-nav', CornerNav);
  if (!customElements.get('product-detail')) customElements.define('product-detail', ProductDetail);
  if (!customElements.get('cart-page')) customElements.define('cart-page', CartPageEl);
  if (!customElements.get('size-chart')) customElements.define('size-chart', SizeChartEl);
  if (!customElements.get('campaign-popup')) customElements.define('campaign-popup', CampaignPopup);

  document.querySelectorAll('[data-review-star-picker]').forEach((picker) => {
    const stars = [...picker.querySelectorAll('[data-star]')];
    const form = picker.closest('form');
    let rating = 0;
    const render = (value) => {
      stars.forEach((star) => {
        const active = Number(star.dataset.star) <= value;
        star.classList.toggle('is-active', active);
        star.textContent = active ? '★' : '☆';
      });
    };
    stars.forEach((star) => {
      star.addEventListener('click', () => { rating = Number(star.dataset.star); render(rating); });
      star.addEventListener('mouseenter', () => render(Number(star.dataset.star)));
    });
    picker.addEventListener('mouseleave', () => render(rating));
    form?.addEventListener('submit', (event) => {
      if (rating < 1) {
        event.preventDefault();
        if (liveRegion) liveRegion.textContent = 'Lütfen bir puan seçin.';
        picker.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      const titleInput = form.querySelector('[data-review-title]');
      const bodyField = form.querySelector('[data-review-body]');
      if (bodyField) {
        const stars5 = '★'.repeat(rating) + '☆'.repeat(5 - rating);
        const titleLine = titleInput?.value ? `Başlık: ${titleInput.value}\n` : '';
        bodyField.value = `Puan: ${stars5} (${rating}/5)\n${titleLine}\n${bodyField.value}`;
      }
    });
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-ajax-cart-form]');
    if (!form) return;
    event.preventDefault();

    const optionGroups = [...form.querySelectorAll('[data-option-index]')];
    const missingGroup = optionGroups.find((group) => !group.querySelector('.is-selected'));
    if (missingGroup) {
      missingGroup.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    const submit = form.querySelector('[type="submit"]');
    submit?.classList.add('is-loading');
    submit?.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(`${theme.routes.cartAdd}.js`, { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || 'Ürün sepete eklenemedi.');
      window.location.assign(theme.routes.cart);
    } catch (error) {
      if (liveRegion) liveRegion.textContent = error.message;
      window.alert(error.message);
      submit?.classList.remove('is-loading');
      submit?.removeAttribute('aria-busy');
    }
  });

  const revealObserver = 'IntersectionObserver' in window ? new IntersectionObserver((entries, observer) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-visible');
      observer.unobserve(entry.target);
    });
  }, { threshold: .08, rootMargin: '0px 0px -30px' }) : null;
  document.querySelectorAll('[data-reveal]').forEach((node) => revealObserver ? revealObserver.observe(node) : node.classList.add('is-visible'));

  const splash = document.getElementById('IntroSplash');
  if (splash && !document.documentElement.classList.contains('no-intro')) {
    setTimeout(() => {
      splash.classList.add('is-hidden');
      setTimeout(() => splash.remove(), 700);
    }, 1400);
  } else if (splash) {
    splash.remove();
  }
})();
